import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { DataTableColumnDefinition, DataTableRow } from '@/features/dataTable/types';

export type DataTableFetchPayload = {
  dataTableName: string;
  parentEntityType?: string;
  parentEntityId?: number | string;
  pageNumber?: number;
  pageSize?: number;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
};

export type DataTableFetchSuccessPayload = {
  rows: DataTableRow[];
  columns: DataTableColumnDefinition[];
  totalCount: number;
};

export type DataTableDeleteRowPayload = {
  dataTableName: string;
  rowId: string | number;
};

export type DataTableCreateRowPayload = {
  dataTableName: string;
  parentEntityType?: string;
  parentEntityKey?: number | string;
  properties: Record<string, unknown>;
};

export type DataTableState = {
  rows: DataTableRow[];
  columns: DataTableColumnDefinition[];
  totalCount: number;
  isLoading: boolean;
  isDeleting: boolean;
  isCreating: boolean;
  error: string | null;
  deleteError: string | null;
  createError: string | null;
  currentDataTableName: string | null;
  parentEntityType: string | null;
  parentEntityId: number | string | null;
};

const initialState: DataTableState = {
  rows: [],
  columns: [],
  totalCount: 0,
  isLoading: false,
  isDeleting: false,
  isCreating: false,
  error: null,
  deleteError: null,
  createError: null,
  currentDataTableName: null,
  parentEntityType: null,
  parentEntityId: null,
};

const dataTableSlice = createSlice({
  name: 'dataTable',
  initialState,
  reducers: {
    fetchDataTable: (state, action: PayloadAction<DataTableFetchPayload>) => {
      state.isLoading = true;
      state.error = null;
      state.currentDataTableName = action.payload.dataTableName;
      state.parentEntityType = action.payload.parentEntityType ?? null;
      state.parentEntityId = action.payload.parentEntityId ?? null;
    },
    fetchDataTableSuccess: (state, action: PayloadAction<DataTableFetchSuccessPayload>) => {
      state.rows = action.payload.rows;
      state.columns = action.payload.columns;
      state.totalCount = action.payload.totalCount;
      state.isLoading = false;
      state.error = null;
    },
    fetchDataTableFailed: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    resetDataTable: () => initialState,
    // Delete row actions
    deleteDataTableRow: (state, _action: PayloadAction<DataTableDeleteRowPayload>) => {
      state.isDeleting = true;
      state.deleteError = null;
    },
    deleteDataTableRowSuccess: (state, action: PayloadAction<string | number>) => {
      state.rows = state.rows.filter((row) => row.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
      state.isDeleting = false;
      state.deleteError = null;
    },
    deleteDataTableRowFailed: (state, action: PayloadAction<string>) => {
      state.isDeleting = false;
      state.deleteError = action.payload;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    // Create row actions
    createDataTableRow: (state, _action: PayloadAction<DataTableCreateRowPayload>) => {
      state.isCreating = true;
      state.createError = null;
    },
    createDataTableRowSuccess: (state, _action: PayloadAction<DataTableRow>) => {
      // Don't add the row here - we re-fetch the data table after creation
      // to get proper IDs and all fields from the backend
      state.isCreating = false;
      state.createError = null;
    },
    createDataTableRowFailed: (state, action: PayloadAction<string>) => {
      state.isCreating = false;
      state.createError = action.payload;
    },
    clearCreateError: (state) => {
      state.createError = null;
    },
  },
});

export const {
  fetchDataTable,
  fetchDataTableSuccess,
  fetchDataTableFailed,
  resetDataTable,
  deleteDataTableRow,
  deleteDataTableRowSuccess,
  deleteDataTableRowFailed,
  clearDeleteError,
  createDataTableRow,
  createDataTableRowSuccess,
  createDataTableRowFailed,
  clearCreateError,
} = dataTableSlice.actions;

export const dataTableReducer = dataTableSlice.reducer;
export const dataTableReducerPath = dataTableSlice.reducerPath;
